/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/hash/KavenCRC.ts
 * @create:      2019-04-02 17:02:48.463
 * @modify:      2025-10-14 22:39:26.167
 * @version:     6.1.0
 * @times:       84
 * @lines:       342
 * @copyright:   Copyright © 2019-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { ArraySplit } from "../array/ArraySplit";
import { BitString } from "../class/BitString";
import { InitializeAsyncDataAcquisition, InitializeSyncDataAcquisition } from "../Initialize";
import { ICyclicRedundancyCheckModel } from "../interface/ICyclicRedundancyCheckModel";
import { ILoggingAgent } from "../interface/ILoggingAgent";
import { TAsyncHashInput, TSyncHashInput } from "../type/advanced";

export class KavenCRC implements ICyclicRedundancyCheckModel {
    public static get CRC32(): ICyclicRedundancyCheckModel {
        return {
            Width: 32,
            Polynomial: new BitString(0x4C11DB7),
            InitialValue: new BitString(0xffffffff),
            FinalXORValue: new BitString(0xffffffff),
            InputReflected: true,
            ResultReflected: true,
        };
    }

    /**
     * The ECMA polynomial, defined in ECMA 182.
     * @link http://reveng.sourceforge.net/crc-catalogue/17plus.htm#crc.cat.crc-64-ecma-182
     */
    public static get CRC64_ECMA_182(): ICyclicRedundancyCheckModel {
        return {
            Width: 64,
            Polynomial: new BitString("42F0E1EBA9EA3693", 16),
            InitialValue: new BitString(0),
            FinalXORValue: new BitString(0),
            InputReflected: false,
            ResultReflected: false,
        };
    }

    /**
     * Wolfgang Ehrhardt
     */
    public static get CRC64_ECMA_WE(): ICyclicRedundancyCheckModel {
        return {
            Width: 64,
            Polynomial: new BitString("42F0E1EBA9EA3693", 16),
            InitialValue: new BitString("F".repeat(16), 16),
            FinalXORValue: new BitString("F".repeat(16), 16),
            InputReflected: false,
            ResultReflected: false,
        };
    }

    /**
     * XZ Utils
     */
    public static get CRC64_ECMA_XZ(): ICyclicRedundancyCheckModel {
        return {
            Width: 64,
            Polynomial: new BitString("42F0E1EBA9EA3693", 16),
            InitialValue: new BitString("F".repeat(16), 16),
            FinalXORValue: new BitString("F".repeat(16), 16),
            InputReflected: true,
            ResultReflected: true,
        };
    }

    /**
     * The ISO polynomial, defined in ISO 3309 and used in HDLC.
     */
    public static get CRC64_ISO(): ICyclicRedundancyCheckModel {
        return {
            Width: 64,
            Polynomial: new BitString(0x1B),
            InitialValue: new BitString("F".repeat(16), 16),
            FinalXORValue: new BitString("F".repeat(16), 16),
            InputReflected: true,
            ResultReflected: true,
        };
    }

    public Width: number;

    public Polynomial: BitString;
    public InitialValue: BitString;
    public FinalXORValue: BitString;
    public InputReflected: boolean;
    public ResultReflected: boolean;
    private readonly table: BitString[];

    public get ShiftBits() {
        return this.Width - 8;
    }

    public get TopBitMask() {
        return new BitString(1).LeftShift(this.Width - 1);
    }

    public get FullBitMask() {
        return new BitString("1".repeat(this.Width), 2);
    }

    public constructor(modal: ICyclicRedundancyCheckModel) {
        this.Width = modal.Width;

        this.Polynomial = modal.Polynomial;
        this.InitialValue = modal.InitialValue;
        this.FinalXORValue = modal.FinalXORValue;
        this.InputReflected = modal.InputReflected;
        this.ResultReflected = modal.ResultReflected;

        this.table = [];
    }

    public MakeTable(): BitString[] {
        if (this.table.length > 0) {
            return this.table;
        }

        for (let n = 0; n < 256; n++) {
            let c = new BitString(n).LeftShift(this.ShiftBits);
            for (let k = 0; k < 8; k++) {
                if (c.AND(this.TopBitMask).AnyBit()) {
                    c = c.LeftShift(1);
                    c = c.XOR(this.Polynomial);
                } else {
                    c = c.LeftShift(1);
                }
            }
            this.table[n] = c.AND(this.FullBitMask).SetLength(this.Width);
        }

        return this.table;
    }

    /* istanbul ignore next */
    public PrintTable(logger: ILoggingAgent, columns = 8) {
        for (const item of ArraySplit(this.MakeTable(), columns, true)) {
            logger.Info(item.map(p => p.HEX).join(", "), { Raw: true });
        }
    }

    public Compute(input: TSyncHashInput): BitString {
        this.MakeTable();

        let crc = this.InitialValue;

        const data = InitializeSyncDataAcquisition(input);

        let offset = 0;
        const size = Math.min((data.BytesPerRead !== undefined && data.BytesPerRead > 0) ? data.BytesPerRead : 4096, data.TotalLengthInBytes);

        do {
            const bytes = data.Acquire(offset, size);
            for (const byte of bytes) {
                let current = new BitString(byte);

                if (this.InputReflected) {
                    current = current.Reflect(8);
                }

                current = current.LeftShift(this.ShiftBits);

                // update the MSB of crc value with next input byte
                crc = crc.XOR(current);

                // this MSB byte value is the index into the lookup table
                const pos = crc.RightShift(this.ShiftBits).AND(0xff).ToNumber();

                // shift out this index
                crc = crc.LeftShift(8).AND(this.FullBitMask);

                //  XOR-in remainder from lookup table using the calculated index
                crc = crc.XOR(this.table[pos]);
            }

            offset += size;
        } while (offset < data.TotalLengthInBytes);

        if (this.ResultReflected) {
            crc = crc.Reflect(this.Width);
        }

        return crc.XOR(this.FinalXORValue).SetLength(this.Width);
    }

    public async ComputeAsync(input: TAsyncHashInput): Promise<BitString> {
        this.MakeTable();

        let crc = this.InitialValue;

        const data = InitializeAsyncDataAcquisition(input);

        let offset = 0;
        const size = Math.min((data.BytesPerRead !== undefined && data.BytesPerRead > 0) ? data.BytesPerRead : 4096, data.TotalLengthInBytes);

        do {
            const bytes = await data.AcquireAsync(offset, size);
            for (const byte of bytes) {
                let current = new BitString(byte);

                if (this.InputReflected) {
                    current = current.Reflect(8);
                }

                current = current.LeftShift(this.ShiftBits);

                // update the MSB of crc value with next input byte
                crc = crc.XOR(current);

                // this MSB byte value is the index into the lookup table
                const pos = crc.RightShift(this.ShiftBits).AND(0xff).ToNumber();

                // shift out this index
                crc = crc.LeftShift(8).AND(this.FullBitMask);

                //  XOR-in remainder from lookup table using the calculated index
                crc = crc.XOR(this.table[pos]);
            }

            offset += size;
        } while (offset < data.TotalLengthInBytes);

        if (this.ResultReflected) {
            crc = crc.Reflect(this.Width);
        }

        return crc.XOR(this.FinalXORValue).SetLength(this.Width);
    }

    public ComputerWithoutTable(input: TSyncHashInput): BitString {
        let crc = this.InitialValue;

        const data = InitializeSyncDataAcquisition(input);

        let offset = 0;
        const size = (data.BytesPerRead !== undefined && data.BytesPerRead > 0) ? data.BytesPerRead : Math.min(4096, data.TotalLengthInBytes);

        do {
            const bytes = data.Acquire(offset, size);
            for (const byte of bytes) {
                let current = new BitString(byte);

                if (this.InputReflected) {
                    current = current.Reflect(8);
                }

                current = current.LeftShift(this.ShiftBits);

                // update the MSB of crc value with next input byte
                crc = crc.XOR(current);

                for (let i = 0; i < 8; i++) {
                    if (crc.AND(this.TopBitMask).AnyBit()) {
                        crc = crc.LeftShift(1);
                        crc = crc.XOR(this.Polynomial);
                    } else {
                        crc = crc.LeftShift(1);
                    }
                }
            }

            offset += size;
        } while (offset < data.TotalLengthInBytes);

        if (this.ResultReflected) {
            crc = crc.Reflect(this.Width);
        }

        return crc.XOR(this.FinalXORValue).SetLength(this.Width);
    }

    public async ComputerWithoutTableAsync(input: TAsyncHashInput): Promise<BitString> {
        let crc = this.InitialValue;

        const data = InitializeAsyncDataAcquisition(input);

        let offset = 0;
        const size = (data.BytesPerRead !== undefined && data.BytesPerRead > 0) ? data.BytesPerRead : Math.min(4096, data.TotalLengthInBytes);

        do {
            const bytes = await data.AcquireAsync(offset, size);
            for (const byte of bytes) {
                let current = new BitString(byte);

                if (this.InputReflected) {
                    current = current.Reflect(8);
                }

                current = current.LeftShift(this.ShiftBits);

                // update the MSB of crc value with next input byte
                crc = crc.XOR(current);

                for (let i = 0; i < 8; i++) {
                    if (crc.AND(this.TopBitMask).AnyBit()) {
                        crc = crc.LeftShift(1);
                        crc = crc.XOR(this.Polynomial);
                    } else {
                        crc = crc.LeftShift(1);
                    }
                }
            }

            offset += size;
        } while (offset < data.TotalLengthInBytes);

        if (this.ResultReflected) {
            crc = crc.Reflect(this.Width);
        }

        return crc.XOR(this.FinalXORValue).SetLength(this.Width);
    }
}
